import { CheckCircle, Sparkle } from "@phosphor-icons/react";
import { AspectRatio } from "@reactive-resume/ui";
import type { Template } from "@reactive-resume/utils";
import { cn, templatesList } from "@reactive-resume/utils";
import { motion } from "framer-motion";

import { templateCardUrl, templateImageUrl } from "@/client/constants/template-images";
import { applyTemplateThemeToData } from "@/client/constants/template-presets";
import { useResumeStore } from "@/client/stores/resume";

export const TemplateSection = () => {
  const setData = useResumeStore((state) => state.setData);
  const currentTemplate = useResumeStore((state) => state.resume.data.metadata.template);
  // find similar templates for this Activity and this profile by current template

  const applyTemplate = (template: string) => {
    const data = useResumeStore.getState().resume.data;
    setData(applyTemplateThemeToData(data, template as Template));
  };

  return (
    <section id="template" className="grid min-w-0 gap-y-4 px-6 pb-6 sm:px-8">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-100 px-3 py-1 text-xs font-semibold text-zinc-500">
          <Sparkle size={13} weight="duotone" />
          {templatesList.length} modèles disponibles
        </span>
      </div>

      <main className="grid max-h-[min(500px,calc(100dvh-17rem))] grid-cols-2 gap-4 overflow-y-auto pr-1 sm:gap-5 lg:grid-cols-3">
        {templatesList.map((template, index) => (
          <AspectRatio key={template} ratio={1 / 1.4142}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { delay: index * 0.1 } }}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98, transition: { duration: 0.1 } }}
              className={cn(
                "group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border-2 bg-white text-left transition-all duration-300",
                currentTemplate === template
                  ? "border-[#472cb3] shadow-[0_18px_44px_rgba(71,44,179,0.18)] ring-4 ring-[#472cb3]/15"
                  : "border-zinc-200 hover:border-[#472cb3]/50 hover:shadow-lg",
              )}
              onClick={() => {
                applyTemplate(template);
              }}
            >
              <div className="flex h-full items-start justify-center overflow-hidden bg-gradient-to-br from-zinc-100 to-zinc-200/70 p-2.5">
                <div className="relative h-full w-full overflow-hidden rounded-lg bg-white shadow-sm">
                  <img
                    src={templateCardUrl(template)}
                    alt={`Aperçu du modèle ${template}`}
                    loading="lazy"
                    decoding="async"
                    onError={(event) => {
                      const image = event.currentTarget;
                      const reference = templateImageUrl(template);
                      if (!image.src.endsWith(reference)) image.src = reference;
                    }}
                    className="object-top-left h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                  <span
                    className={cn(
                      "absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-white shadow-md transition-all duration-200",
                      currentTemplate === template
                        ? "scale-100 bg-[#472cb3] opacity-100"
                        : "scale-75 bg-zinc-900/50 opacity-0",
                    )}
                  >
                    <CheckCircle weight="bold" size={15} />
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 border-t border-zinc-100 px-3.5 py-2.5">
                <span className="truncate text-sm font-bold capitalize tracking-wide text-zinc-700">
                  {template}
                </span>
                <span
                  className={cn(
                    "shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-colors",
                    currentTemplate === template
                      ? "bg-[#3ecf8e]/15 text-[#0e9f6e]"
                      : "bg-zinc-100 text-zinc-400",
                  )}
                >
                  {currentTemplate === template ? "Sélectionné" : "Aperçu"}
                </span>
              </div>
            </motion.div>
          </AspectRatio>
        ))}
      </main>
    </section>
  );
};
